import {
  Box,
  Chip,
  IconButton,
  Paper,
  SvgIconProps,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Typography,
} from '@mui/material'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import { sortBy } from 'lodash'
import { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Mutation_Root, Project_Expert, Project_User, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { EditUserPermissionModal } from 'src/screens/shared/permission-users/EditUserPermissionModal'
import { AddExternalExpertModalDialog } from 'src/screens/shared/project/permissions/AddExternalExpertModalDialog'
import { AddNewUserModalDialog } from 'src/screens/shared/project/permissions/AddNewUserModalDialog'
import {
  deleteProjectExpertFromProject,
  deleteUserFromProject,
  queryPfKapProjectUsersAndUserType,
  queryPfPgvProjectExperts,
  queryPfPgvProjectUsersAndUserType,
  updateProjectUser,
} from 'src/screens/shared/project/permissions/projectPermissionsQueries'
import { resolveUserPermissionMessageKey } from 'src/screens/shared/project/permissions/ProjectPermissionsUtils'
import { usePermissionsForProject } from 'src/service/security/PermissionHook'
import { AddButton } from 'src/shared/button/Buttons'
import { PROJECT, PROJECT_USER_ROLES, USER_STATUS } from 'src/shared/constants/constants'
import { Option } from 'src/shared/form/control'
import { DeleteIcon, EditIcon, PersonAddIcon, PersonIcon } from 'src/shared/icons/Icons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { ProjectExportMenu } from 'src/shared/menu/ProjectExportMenu'
import { ConfirmationModalDialog } from 'src/shared/modal-dialog/ConfirmationModalDialog'
import { ModalDialog } from 'src/shared/modal-dialog/ModalDialog'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Section } from 'src/shared/presentation/Section'
import { S } from 'src/shared/styled/S'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { Utils } from 'src/shared/utils/Utils'
import { UserContext } from 'src/user/UserContext'
import styled from 'styled-components'
import { useClient } from 'urql'

interface ProjectPermissionsProps {
  projectId: number
  baseUrl: '/pf-kap' | '/pf-pgv'
}

const StyledTableRow = styled(TableRow)`
  & .MuiTableCell-root {
    padding-top: ${({ theme }) => theme.spacing(1.5)};
    padding-bottom: ${({ theme }) => theme.spacing(1.5)};
  }
`

const StyledTableCell = styled(TableCell)<{ $active?: boolean }>`
  color: ${({ $active, theme }) => ($active ? theme.colors.text.primary : theme.colors.text.secondary)};
`

const StyledChip = styled(Chip)<{ $active: boolean } & SvgIconProps>`
  background-color: ${({ $active, theme }) => ($active ? theme.colors.success.light : theme.colors.grey.background)};
  color: ${({ $active, theme }) => ($active ? theme.colors.success.dark : theme.colors.text.secondary)};
`
export const ProjectPermissions = ({ projectId, baseUrl }: ProjectPermissionsProps): ReactElement => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const { user } = useContext(UserContext)
  const notificationService = useNotificationService()

  const projectType = Utils.resolveProcess(baseUrl)

  const [projectUsers, setProjectUsers] = useState<Project_User[] | undefined>()
  const [projectExternalEvaluators, setProjectExternalEvaluators] = useState<Project_Expert[] | undefined>()
  const [projectBaseId, setProjectBaseId] = useState<number | undefined>()
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false)
  const [openAddExternalExpertDialog, setOpenAddExternalExpertDialog] = useState(false)
  const [openDeleteUserDialog, setOpenDeleteUserDialog] = useState(false)
  const [openEditUserDialog, setOpenEditUserDialog] = useState(false)
  const [selectedUserForAction, setSelectedUserForAction] = useState<Project_User | null>(null)
  const [selectedExpertForAction, setSelectedExpertForAction] = useState<Project_Expert | null>(null)
  const [openDeleteExpertDialog, setOpenDeleteExpertDialog] = useState(false)

  const projectPermissionOptions = useMemo<Option[]>(() => {
    return [
      { label: getMessage('label.can.edit'), value: PROJECT_USER_ROLES.CONTRIBUTOR },
      { label: getMessage('label.can.view'), value: PROJECT_USER_ROLES.READER },
    ]
  }, [getMessage])

  const { loading, canView, canModifyProjectUsers, canModifyProjectExperts } = usePermissionsForProject(
    projectType,
    projectId,
  )

  const isUserActive = (user: Project_User | Project_Expert): boolean => user.user.status === USER_STATUS.ACTIVE

  const fetchProjectUsers = useCallback(async () => {
    let fetchedProjectUsers
    let projectBaseId
    if (baseUrl === '/pf-kap') {
      const { data, error } = await urqlClient
        .query<
          {
            projectUsers: Query_Root['pf_kap_project_by_pk']
          },
          {
            id: number
          }
        >(queryPfKapProjectUsersAndUserType, { id: projectId })
        .toPromise()

      if (error) {
        notificationService.operationFailed()
        return
      }
      fetchedProjectUsers = data?.projectUsers?.project_base.project_users
      projectBaseId = data?.projectUsers?.project_base.id
    } else if (baseUrl === '/pf-pgv') {
      const { data, error } = await urqlClient
        .query<
          {
            projectUsers: Query_Root['pf_pgv_project_by_pk']
          },
          {
            id: number
          }
        >(queryPfPgvProjectUsersAndUserType, { id: projectId })
        .toPromise()
      if (error) {
        notificationService.operationFailed()
        return
      }
      fetchedProjectUsers = data?.projectUsers?.project_base.project_users
      projectBaseId = data?.projectUsers?.project_base.id
    }
    const filteredFetchedProjectUsers = fetchedProjectUsers?.filter((user) => user.type !== PROJECT_USER_ROLES.EXPERT)
    setProjectUsers(sortBy(filteredFetchedProjectUsers, [(pu: Project_User) => pu.type !== PROJECT_USER_ROLES.ADMIN]))
    setProjectBaseId(projectBaseId)
  }, [baseUrl, projectId, urqlClient, notificationService])

  const fetchProjectExperts = useCallback(async () => {
    if (baseUrl === '/pf-pgv') {
      const { data, error } = await urqlClient
        .query<
          {
            projectExperts: Query_Root['pf_pgv_project_by_pk']
          },
          {
            id: number
          }
        >(queryPfPgvProjectExperts, { id: projectId })
        .toPromise()
      if (error) {
        notificationService.operationFailed()
        return
      }
      setProjectExternalEvaluators(data?.projectExperts?.project_base.project_experts)
    }
  }, [baseUrl, notificationService, projectId, urqlClient])

  useEffect(() => {
    const initData = async () => {
      await fetchProjectUsers()
      await fetchProjectExperts()
    }
    initData()
  }, [baseUrl, fetchProjectExperts, fetchProjectUsers, projectId, urqlClient])

  // handler for add user action
  const handleOpenAddNewUserDialog = () => {
    setOpenAddUserDialog(true)
  }

  const handleCloseAddUserDialog = () => {
    setOpenAddUserDialog(false)
    setSelectedUserForAction(null)
  }

  const handleAfterSuccessAddUser = async () => {
    handleCloseAddUserDialog()
    await fetchProjectUsers()
  }

  // handler for delete user action
  const handleOpenDeleteUserDialog = (projectUser: Project_User) => () => {
    setSelectedUserForAction(projectUser)
    setOpenDeleteUserDialog(true)
  }
  const handleCloseDeleteUserDialog = () => {
    setOpenDeleteUserDialog(false)
    setSelectedUserForAction(null)
  }

  const handleDeleteUserConfirmed = async () => {
    const { data, error } = await urqlClient
      .mutation<{ delete_project_user: Mutation_Root['delete_project_user'] }>(deleteUserFromProject, {
        projectBaseId: projectBaseId,
        userId: selectedUserForAction?.user.id,
      })
      .toPromise()
    if (error || data?.delete_project_user?.affected_rows === 0) {
      notificationService.operationFailed()
    } else {
      notificationService.deleteSuccessful()
      await fetchProjectUsers()
    }

    handleCloseDeleteUserDialog()
  }

  // handler for edit user action
  const handleOpenEditUserDialog = (projectUser: Project_User) => () => {
    setSelectedUserForAction(projectUser)
    setOpenEditUserDialog(true)
  }

  const handleCloseEditUserDialog = () => {
    setOpenEditUserDialog(false)
    setSelectedUserForAction(null)
  }

  const handleEditUserPermission = async (value: string) => {
    if (selectedUserForAction?.id) {
      const { data, error } = await urqlClient
        .mutation<{ update_project_user: Mutation_Root['update_project_user'] }>(updateProjectUser, {
          projectUserId: selectedUserForAction?.id,
          type: value,
        })
        .toPromise()

      if (error || data?.update_project_user?.affected_rows === 0) {
        notificationService.operationFailed()
      } else {
        notificationService.changesSaved()
        await fetchProjectUsers()
      }

      handleCloseEditUserDialog()
    }
  }

  const handleDeleteProjectExpertConfirmed = async () => {
    const { data, error } = await urqlClient
      .mutation<{ delete_project_expert: Mutation_Root['delete_project_expert'] }>(deleteProjectExpertFromProject, {
        projectBaseId: projectBaseId,
        userId: selectedExpertForAction?.user.id,
      })
      .toPromise()
    if (error || data?.delete_project_expert?.affected_rows === 0) {
      notificationService.operationFailed()
    } else {
      notificationService.deleteSuccessful()
      await fetchProjectExperts()
    }

    handleCloseDeleteExpertDialog()
  }

  // handler for add external expert action
  const handleOpenAddExternalExpertDialog = () => {
    setOpenAddExternalExpertDialog(true)
  }

  const handleCloseAddExternalExpertDialog = () => {
    setOpenAddExternalExpertDialog(false)
    setSelectedExpertForAction(null)
  }

  const handleAfterSuccessAddExternalExpert = async () => {
    handleCloseAddExternalExpertDialog()
    await fetchProjectExperts()
  }

  // handler for delete expert action
  const handleOpenDeleteExpertDialog = (projectExpert: Project_Expert) => () => {
    setSelectedExpertForAction(projectExpert)
    setOpenDeleteExpertDialog(true)
  }
  const handleCloseDeleteExpertDialog = () => {
    setOpenDeleteExpertDialog(false)
    setSelectedExpertForAction(null)
  }

  if (!loading && !canView) {
    return <NotAuthorized />
  }

  return (
    <>
      {!loading && (
        <ScreenLayout
          title={getMessage('label.navigation.project.permissions')}
          hasSecondLevelNavigation={false}
          actions={<ProjectExportMenu process={projectType} entityId={projectBaseId as number} />}
        >
          <PageLayout>
            {!loading && (
              <>
                <Section
                  title={getMessage('label.project.permissions.users')}
                  actionButton={
                    canModifyProjectUsers && (
                      <AddButton
                        messageKey="button.user.add"
                        startIcon={<PersonAddIcon />}
                        onClick={handleOpenAddNewUserDialog}
                      />
                    )
                  }
                  helpAndInstructions={
                    <HelpAndInstructions labelKey={`label.help.project.permissions.${projectType}`} />
                  }
                >
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>{getMessage('label.name')}</TableCell>
                          <TableCell>{getMessage('label.permissions')}</TableCell>
                          <TableCell>{getMessage('label.status')}</TableCell>
                          {canModifyProjectUsers && <TableCell align="right">{getMessage('label.actions')}</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {projectUsers?.map((u, i) => (
                          <StyledTableRow key={i}>
                            <StyledTableCell $active={isUserActive(u)}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <S.Avatar $randomColorKey={isUserActive(u) ? u?.user.email : ''}>
                                  {isUserActive(u) ? u.user.first_name?.charAt(0) : <PersonIcon />}
                                </S.Avatar>
                                <S.DataGrid.TwoLineCell>
                                  <Typography variant="subtitle2">
                                    {isUserActive(u)
                                      ? `${u.user.first_name} ${u.user.last_name}`
                                      : getMessage('label.user.unknown')}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {u.user.email}
                                  </Typography>
                                </S.DataGrid.TwoLineCell>
                              </Box>
                            </StyledTableCell>
                            <StyledTableCell $active={isUserActive(u)}>
                              {getMessage(resolveUserPermissionMessageKey(u.type))}
                            </StyledTableCell>
                            <StyledTableCell $active={isUserActive(u)}>
                              <StyledChip
                                $active={isUserActive(u)}
                                label={getMessage(`label.${u.user.status.toLowerCase()}`)}
                              />
                            </StyledTableCell>

                            {canModifyProjectUsers && (
                              <StyledTableCell align="right">
                                <Box display={'flex'} gap={1} justifyContent={'end'}>
                                  {user.email !== u.user.email && u.type !== PROJECT_USER_ROLES.ADMIN && (
                                    <IconButton color="primary" onClick={handleOpenEditUserDialog(u)}>
                                      <EditIcon />
                                    </IconButton>
                                  )}

                                  {user.email !== u.user.email && u.type !== PROJECT_USER_ROLES.ADMIN && (
                                    <IconButton color="primary" onClick={handleOpenDeleteUserDialog(u)}>
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </Box>
                              </StyledTableCell>
                            )}
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {projectBaseId && (
                    <ModalDialog
                      open={openAddUserDialog}
                      title={getMessage('label.add.new.user')}
                      maxWidth="sm"
                      withCloseIcon={false}
                      onClose={handleCloseAddUserDialog}
                    >
                      <AddNewUserModalDialog
                        onCancel={handleCloseAddUserDialog}
                        onSuccess={handleAfterSuccessAddUser}
                        projectBaseId={projectBaseId}
                      />
                    </ModalDialog>
                  )}

                  <ConfirmationModalDialog
                    open={openDeleteUserDialog}
                    onCancel={handleCloseDeleteUserDialog}
                    onConfirm={handleDeleteUserConfirmed}
                  >
                    {getMessage('label.delete.confirm')}
                  </ConfirmationModalDialog>

                  {selectedUserForAction && (
                    <ModalDialog
                      maxWidth="sm"
                      open={openEditUserDialog}
                      onClose={handleCloseEditUserDialog}
                      title={getMessage('label.edit.user.permission')}
                    >
                      <EditUserPermissionModal
                        userPermissionsOptions={projectPermissionOptions}
                        userPermission={selectedUserForAction?.type}
                        onConfirm={handleEditUserPermission}
                        onCancel={handleCloseEditUserDialog}
                      />
                    </ModalDialog>
                  )}
                </Section>

                {projectType === PROJECT.PF_PGV && (
                  <Section
                    title={getMessage('label.project.permissions.experts')}
                    actionButton={
                      canModifyProjectExperts && (
                        <AddButton
                          messageKey="label.add.external.expert"
                          startIcon={<PersonAddIcon />}
                          onClick={handleOpenAddExternalExpertDialog}
                        />
                      )
                    }
                    helpAndInstructions={
                      <HelpAndInstructions labelKey={`label.help.project.permissions.expert.table`} />
                    }
                  >
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{getMessage('label.name')}</TableCell>
                            <TableCell>{getMessage('label.permissions')}</TableCell>
                            <TableCell>{getMessage('label.status')}</TableCell>
                            {canModifyProjectExperts && (
                              <TableCell align="right">{getMessage('label.actions')}</TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {projectExternalEvaluators?.map((u, i) => (
                            <StyledTableRow key={i}>
                              <StyledTableCell $active={isUserActive(u)}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <S.Avatar $randomColorKey={isUserActive(u) ? u?.user.email : ''}>
                                    {isUserActive(u) ? u.user.first_name?.charAt(0) : <PersonIcon />}
                                  </S.Avatar>
                                  <S.DataGrid.TwoLineCell>
                                    <Typography variant="subtitle2">
                                      {isUserActive(u)
                                        ? `${u.user.first_name} ${u.user.last_name}`
                                        : getMessage('label.user.unknown')}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      {u.user.email}
                                    </Typography>
                                  </S.DataGrid.TwoLineCell>
                                </Box>
                              </StyledTableCell>
                              <StyledTableCell $active={isUserActive(u)}>
                                {getMessage('label.role.external.evaluation.team.pgv')}
                              </StyledTableCell>
                              <StyledTableCell $active={isUserActive(u)}>
                                <StyledChip
                                  $active={isUserActive(u)}
                                  label={getMessage(`label.${u.user.status.toLowerCase()}`)}
                                />
                              </StyledTableCell>

                              {canModifyProjectExperts && (
                                <StyledTableCell align="right">
                                  <Box display={'flex'} gap={1} justifyContent={'end'}>
                                    <IconButton color="primary" onClick={handleOpenDeleteExpertDialog(u)}>
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                </StyledTableCell>
                              )}
                            </StyledTableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {projectBaseId && (
                      <ModalDialog
                        open={openAddExternalExpertDialog}
                        title={getMessage('label.add.external.expert')}
                        maxWidth="sm"
                        withCloseIcon={false}
                        onClose={handleCloseAddExternalExpertDialog}
                      >
                        <AddExternalExpertModalDialog
                          assignedExperts={projectExternalEvaluators ?? []}
                          onCancel={handleCloseAddExternalExpertDialog}
                          onSuccess={handleAfterSuccessAddExternalExpert}
                          projectBaseId={projectBaseId}
                        />
                      </ModalDialog>
                    )}

                    <ConfirmationModalDialog
                      open={openDeleteExpertDialog}
                      onCancel={handleCloseDeleteExpertDialog}
                      onConfirm={handleDeleteProjectExpertConfirmed}
                    >
                      {getMessage('label.delete.confirm')}
                    </ConfirmationModalDialog>
                  </Section>
                )}
              </>
            )}
          </PageLayout>
        </ScreenLayout>
      )}
    </>
  )
}
